/**
 * The one error type every API call raises.
 *
 * Callers branch on `code` (stable, machine-readable) and never on `message`
 * (human text that may change or be localised by the server).
 */
export const NETWORK_ERROR = 'NETWORK_UNREACHABLE';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;
  /** The server's `error.retryable`, when it said. */
  readonly retryable: boolean | null;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    details?: Record<string, unknown> | undefined;
    requestId?: string | null | undefined;
    retryable?: boolean | null | undefined;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details ?? {};
    this.requestId = init.requestId ?? null;
    this.retryable = init.retryable ?? null;
  }

  /** The request never reached the server (offline, DNS, captive portal, timeout). */
  get isNetwork(): boolean {
    return this.code === NETWORK_ERROR;
  }

  /** Safe for the outbox to replay automatically later. */
  get isRetryable(): boolean {
    if (this.isNetwork) return true;
    if (this.retryable !== null) return this.retryable;
    return this.status >= 500 || this.status === 429 || this.status === 408;
  }

  /** The person must sign in again; retrying will not help. */
  get needsAuth(): boolean {
    return this.status === 401;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
